/**
 * @element qds-theme-switch
 * 
 * A toggle switch component for changing between light and dark themes.
 * Theme preference is automatically saved and restored across sessions.
 * 
 * @attr {boolean} dark - Whether the current theme is dark
 * @attr {boolean} auto - Whether to use system preferences (if supported)
 * @attr {boolean} compact - When true, shows only the theme toggle button without the auto button
 * 
 * @csspart button - The button container
 * @csspart icon - The theme icon
 * 
 * @fires theme-change - When the theme is switched, with detail { dark: boolean, theme: string }
 */

import { adoptPrimeIconsStyles, getBaseButtonStyles } from '../shared/button-utils.js';
import themeManager from '../shared/theme-manager.js';
import { Theme, ThemeChangeEventDetail } from '../../types/index';

// Extended theme change event including auto preference
interface ThemeSwitchEventDetail extends ThemeChangeEventDetail {
  auto?: boolean;
}

export class QdsThemeSwitch extends HTMLElement {
  // Private properties
  private _dark: boolean;
  private _auto: boolean;
  private _compact: boolean;
  private _cleanup?: () => void;

  static get observedAttributes(): string[] {
    return ['dark', 'auto', 'compact'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // Initialize properties
    this._dark = themeManager.isDarkTheme();
    this._auto = themeManager.isAutoTheme();
    this._compact = false;
    
    this.render();
  }

  connectedCallback(): void {
    if (!this.isConnected) return;

    this._compact = this.hasAttribute('compact');
    
    // Apply current state to attributes
    if (this._dark) {
      this.setAttribute('dark', '');
    }
    
    if (this._auto) {
      this.setAttribute('auto', '');
    }
    
    // Set up event handling with updated theme state
    this._setupEventHandlers();
    
    // Listen for theme changes from other sources
    document.addEventListener('qds-theme-change', this._handleThemeChange as EventListener);
  }

  disconnectedCallback(): void {
    // Clean up event listeners
    if (this._cleanup) {
      this._cleanup();
      this._cleanup = undefined;
    }
    
    document.removeEventListener('qds-theme-change', this._handleThemeChange as EventListener);
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'dark':
        this._dark = newValue !== null && newValue !== 'false';
        break;
      case 'auto':
        this._auto = newValue !== null && newValue !== 'false';
        break;
      case 'compact':
        this._compact = newValue !== null && newValue !== 'false';
        break;
    }
    
    this.render();
  }

  get dark(): boolean {
    return this._dark;
  }

  set dark(value: boolean) {
    if (value) {
      this.setAttribute('dark', '');
    } else {
      this.removeAttribute('dark');
    }
  }
  
  get auto(): boolean {
    return this._auto;
  }
  
  set auto(value: boolean) {
    if (value) {
      this.setAttribute('auto', '');
    } else {
      this.removeAttribute('auto');
    }
  }
  
  get compact(): boolean {
    return this._compact;
  }
  
  set compact(value: boolean) {
    if (value) {
      this.setAttribute('compact', '');
    } else {
      this.removeAttribute('compact');
    }
  }
  
  // Handle theme changes from other sources
  private _handleThemeChange = (event: CustomEvent<ThemeChangeEventDetail>): void => {
    const { dark, auto } = event.detail;
    let shouldRender = false;

    if (this._dark !== dark) {
      this._dark = dark;
      
      if (dark) {
        this.setAttribute('dark', '');
      } else {
        this.removeAttribute('dark');
      }

      shouldRender = true;
    }

    if (typeof auto === 'boolean' && this._auto !== auto) {
      this._auto = auto;

      if (auto) {
        this.setAttribute('auto', '');
      } else {
        this.removeAttribute('auto');
      }

      shouldRender = true;
    }

    if (shouldRender) {
      this.render();
    }
  }

  private _setupEventHandlers(): void {
    // Clean up existing listeners if any
    if (this._cleanup) {
      this._cleanup();
    }
    
    // Get the buttons
    const themeButton = this.shadowRoot?.querySelector('#theme-button');
    const autoButton = this.shadowRoot?.querySelector('#auto-button');
    
    // Set up cleanup function
    const cleanupFunctions: Array<() => void> = [];
    
    // Handle theme toggle
    const handleThemeToggle = (): void => {
      const newTheme = themeManager.toggleTheme();
      this._dark = newTheme === 'dark';
      
      // Update attribute
      if (this._dark) {
        this.setAttribute('dark', '');
      } else {
        this.removeAttribute('dark');
      }
      
      // Update auto state
      this._auto = false;
      this.removeAttribute('auto');
      
      this.render();
      
      // Dispatch the event
      this.dispatchEvent(new CustomEvent<ThemeSwitchEventDetail>('theme-change', {
        bubbles: true,
        composed: true,
        detail: { 
          dark: this._dark,
          theme: newTheme,
          auto: false
        }
      }));
    };
    
    // Add theme button click listener
    if (themeButton) {
      themeButton.addEventListener('click', handleThemeToggle);
      cleanupFunctions.push(() => {
        if (themeButton) {
          themeButton.removeEventListener('click', handleThemeToggle);
        }
      });
    }
    
    // Handle auto toggle - only if the auto button exists (non-compact mode)
    if (autoButton) {
      const handleAutoToggle = (): void => {
        const newTheme = themeManager.enableAutoTheme();
        this._dark = newTheme === 'dark';
        
        // Update attributes
        this._auto = true;
        this.setAttribute('auto', '');
        
        if (this._dark) {
          this.setAttribute('dark', '');
        } else {
          this.removeAttribute('dark');
        }
        
        this.render();
        
        // Dispatch the event
        this.dispatchEvent(new CustomEvent<ThemeSwitchEventDetail>('theme-change', {
          bubbles: true,
          composed: true,
          detail: { 
            dark: this._dark,
            theme: newTheme,
            auto: true
          }
        }));
      };
      
      autoButton.addEventListener('click', handleAutoToggle);
      cleanupFunctions.push(() => {
        if (autoButton) {
          autoButton.removeEventListener('click', handleAutoToggle);
        }
      });
    }
    
    // Return cleanup function that calls all cleanup functions
    this._cleanup = () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }

  private render(): void {
    if (!this.shadowRoot) return;

    // Use PrimeIcons for consistency
    const sunIcon = `<i class="pi pi-sun theme-icon"></i>`;
    const moonIcon = `<i class="pi pi-moon theme-icon"></i>`;
    const desktopIcon = `<i class="pi pi-desktop theme-icon"></i>`;

    // Base styles
    const styles = `
      <style>
        ${getBaseButtonStyles()}

        :host {
          display: inline-flex;
          gap: 8px;
        }

        #auto-button {
          opacity: ${this._auto ? '1' : '0.5'};
        }

        .tooltip {
          position: relative;
        }

        .tooltip::after {
          content: attr(data-tooltip);
          position: absolute;
          bottom: -30px;
          left: 50%;
          transform: translateX(-50%);
          background-color: var(--theme-background-elevated, var(--theme-background-tertiary));
          color: var(--theme-text-primary);
          border: 1px solid var(--theme-border-primary);
          box-shadow: var(--theme-shadow-xs);
          padding: 5px 8px;
          border-radius: 4px;
          font-size: 12px;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s;
          white-space: nowrap;
          z-index: 10;
        }

        .tooltip:hover::after {
          opacity: 1;
        }

        .theme-icon {
          font-size: var(--icon-size, 20px);
          color: currentColor;
        }
      </style>
    `;

    // Determine the appropriate HTML based on compact mode
    let html;

    if (this._compact) {
      // Compact mode - only the theme toggle button
      html = `
        ${styles}
        <button id="theme-button" part="button" class="tooltip" data-tooltip="${this._dark ? 'Switch to light theme' : 'Switch to dark theme'}">
          ${this._dark ? sunIcon : moonIcon}
        </button>
      `;
    } else {
      // Full mode - both buttons
      html = `
        ${styles}
        <button id="theme-button" part="button" class="tooltip" data-tooltip="${this._dark ? 'Switch to light theme' : 'Switch to dark theme'}">
          ${this._dark ? sunIcon : moonIcon}
        </button>
        
        <button id="auto-button" part="button" class="tooltip" data-tooltip="Use system preference">
          ${desktopIcon}
        </button>
      `;
    }
    
    this.shadowRoot.innerHTML = html;
    void adoptPrimeIconsStyles(this.shadowRoot);
    
    // Add event listener since we're re-rendering the DOM
    if (this.isConnected) {
      this._setupEventHandlers();
    }
  }
}

// Only define the custom element if it's not already defined
if (!customElements.get('qds-theme-switch')) {
  customElements.define('qds-theme-switch', QdsThemeSwitch);
}
